<!--
  ~ Copyright (C) 2026 - Potentially Ltd
  ~
  ~ Please see distribution for license.
  -->

<div class="v3-member-info">
  <header class="v3-member-info__header">
    <div class="v3-member-info__identity">
      @if (member.imageUrl) {
        <img class="v3-member-info__avatar-img" [src]="member.imageUrl" [alt]="member.firstName + ' ' + member.lastName" />
      } @else {
        <span class="v3-member-info__avatar">{{ initials }}</span>
      }

      <div class="v3-member-info__identity-text">
        <span class="v3-member-info__name">{{ member.firstName + ' ' + member.lastName }}</span>

        <div class="v3-member-info__status">
          <span class="v3-member-info__status-dot" [class.is-alumni]="member.membershipStatus === 'ALUMNI'"></span>
          <span class="v3-member-info__status-label" [class.is-alumni]="member.membershipStatus === 'ALUMNI'">
            @if (member.membershipStatus === 'ALUMNI') {
              {{ 'translations.userRecord.roles.value.alumni' | transloco }}
            } @else {
              {{ 'translations.global.label.active' | transloco }}
            }
          </span>
          @if (member.externalId) {
            <span class="v3-member-info__external-id">{{ member.externalId }}</span>
          }
        </div>
      </div>

      <button
        #closeBtn
        type="button"
        class="f_close-btn v3-member-info__close"
        (click)="closeDialog()"
        [attr.aria-label]="'translations.global.ariaLabels.closeMembersInfoModal' | transloco"
      >
        <ptl-icon [icon]="LucideX" size="m"></ptl-icon>
      </button>
    </div>

    @if (isPlaylistPage) {
      <div class="v3-member-info__context">
        {{ 'translations.global.label.activityOn' | transloco }} <b>{{ (playlist$ | async)?.data?.title }}</b>
      </div>
    }
    @if (isCardPage) {
      <div class="v3-member-info__context">
        {{ 'translations.global.label.activityOn' | transloco }} <b>{{ (resource$ | async)?.content?.headline }}</b>
      </div>
    }
    @if (programTitle) {
      <div class="v3-member-info__context">
        {{ 'translations.global.label.activityOn' | transloco }} <b>{{ programTitle }}</b>
      </div>
    }

    <div class="v3-member-info__actions">
      <ptl-button
        [type]="'primary'"
        [size]="'sm'"
        [icon]="LucideExternalLink"
        [fullWidth]="true"
        [ariaLabel]="'translations.global.ariaLabels.viewProfile' | transloco"
        (clicked)="openMemberDetailPage(member._id)"
      >
        {{ 'translations.members.button.openFullRecord' | transloco }}
      </ptl-button>
      <ptl-button
        [type]="'secondary-neutral'"
        [size]="'sm'"
        [icon]="LucideMessageSquare"
        [ariaLabel]="'translations.global.ariaLabels.openMessage' | transloco"
        (clicked)="openSendMessageDialog()"
      >
        {{ 'translations.message' | transloco }}
      </ptl-button>
    </div>
  </header>

  <div class="v3-member-info__body">
    <section class="v3-member-info__section">
      <h3 class="v3-member-info__section-title overline">
        {{ 'translations.membersNew.info.contactAndActivity' | transloco }}
      </h3>

      <div class="v3-member-info__row">
        <span class="v3-member-info__row-label">{{ 'translations.global.label.email' | transloco }}</span>
        <button
          type="button"
          class="v3-member-info__row-copy"
          [attr.aria-label]="'translations.membersNew.info.copyEmail' | transloco: { email: member.email }"
          (click)="onCopy(member.email)"
        >
          <span class="v3-member-info__row-copy-value">{{ member.email }}</span>
          <ptl-icon class="v3-member-info__row-copy-icon" [icon]="LucideCopy" size="s"></ptl-icon>
        </button>
      </div>
      @if (member.alumniEmail) {
        <div class="v3-member-info__row">
          <span class="v3-member-info__row-label">{{ 'translations.userRecord.label.alumni_email' | transloco }}</span>
          <button
            type="button"
            class="v3-member-info__row-copy"
            [attr.aria-label]="'translations.membersNew.info.copyEmail' | transloco: { email: member.alumniEmail }"
            (click)="onCopy(member.alumniEmail)"
          >
            <span class="v3-member-info__row-copy-value">{{ member.alumniEmail }}</span>
            <ptl-icon class="v3-member-info__row-copy-icon" [icon]="LucideCopy" size="s"></ptl-icon>
          </button>
        </div>
      }
      @if (member.lastActiveOn) {
        <div class="v3-member-info__row">
          <span class="v3-member-info__row-label">{{ 'translations.global.label.lastActive' | transloco }}</span>
          <span class="v3-member-info__row-value">{{ member.lastActiveOn | ptlDate: 'date' }}</span>
        </div>
      }
      @if (member.createdAt) {
        <div class="v3-member-info__row">
          <span class="v3-member-info__row-label">{{ 'translations.global.label.joinDate' | transloco }}</span>
          <span class="v3-member-info__row-value">{{ member.createdAt | ptlDate: 'date' }}</span>
        </div>
      }
      @if (programUid) {
        <div class="v3-member-info__row">
          <span class="v3-member-info__row-label">{{ 'translations.global.label.status' | transloco }}</span>
          <span class="v3-member-info__progress" [attr.data-state]="getMemberCompletionStatus(member)">
            <span class="v3-member-info__progress-dot"></span>
            @switch (getMemberCompletionStatus(member)) {
              @case ('COMPLETED') {
                {{ 'translations.membersNew.list.table.completed' | transloco }}
              }
              @case ('IN_PROGRESS') {
                {{ 'translations.membersNew.list.table.started' | transloco }}
              }
              @default {
                {{ 'translations.membersNew.list.table.notStarted' | transloco }}
              }
            }
          </span>
        </div>
      }
    </section>

    @if (programUid && hasTicketEnrollment) {
      <section class="v3-member-info__section">
        <h3 class="v3-member-info__section-title overline">{{ 'translations.analytics.metric.registration' | transloco }}</h3>
        @if (isEnrolled()) {
          <div class="v3-member-info__registration">
            <span class="v3-member-info__row-value">{{ member.enrollment?.registeredOn | ptlDate: 'date' }}</span>
            <ptl-button [type]="'secondary-alert'" [size]="'sm'" (clicked)="unenroll.emit()">
              {{ 'translations.global.button.cancel' | transloco }}
            </ptl-button>
          </div>
        } @else {
          <ptl-button [type]="'secondary-neutral'" [size]="'sm'" (clicked)="enroll.emit()">
            {{ 'translations.playlist.enrollment.learnerView.labels.register' | transloco }}
          </ptl-button>
        }
      </section>
    }

    @if (roles.length) {
      <section class="v3-member-info__section">
        <h3 class="v3-member-info__section-title overline">{{ 'translations.userRecord.title.roles' | transloco }}</h3>
        <div class="v3-member-info__pills">
          @for (role of visibleRoles; track role) {
            <ptl-pill [active]="true" [color]="'accent'" [size]="'s'">{{ role }}</ptl-pill>
          }
          @if (hiddenRolesCount) {
            <ptl-button [type]="'link-color'" [size]="'xs'" class="v3-member-info__more-pill" (click)="openMemberDetailPage(member._id)">
              +{{ hiddenRolesCount }} {{ 'translations.global.button.more' | transloco }}
            </ptl-button>
          }
        </div>
      </section>
    }

    @if (groups.length) {
      <section class="v3-member-info__section">
        <h3 class="v3-member-info__section-title overline">{{ 'translations.userRecord.label.groups' | transloco }}</h3>
        <div class="v3-member-info__pills">
          @for (group of visibleGroups; track group.groupId) {
            <ptl-pill
              class="v3-member-info__group-pill"
              [color]="'violet'"
              [size]="'s'"
              [matTooltip]="group.title"
              matTooltipClass="medium-tooltip"
              >{{ group.title }}</ptl-pill
            >
          }
          @if (hiddenGroupsCount) {
            <ptl-button [type]="'link-color'" [size]="'xs'" class="v3-member-info__more-pill" (click)="openMemberDetailPage(member._id)">
              +{{ hiddenGroupsCount }} {{ 'translations.global.button.more' | transloco }}
            </ptl-button>
          }
        </div>
      </section>
    }
  </div>
</div>
